/**
 * TrustGrid Onboarding Wizard
 *
 * 6-step guided setup for new organizations
 */
import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button, Form, Input, Select, Radio, Card, Alert, Spin, Divider, Typography, Space, Tag } from 'antd';
import { CheckCircleOutlined, RocketOutlined, ApiOutlined, SafetyCertificateOutlined, SettingOutlined, ScanOutlined, TrophyOutlined } from '@ant-design/icons';
import {
  createProject,
  listConnectors,
  createConnector,
  listControls,
  assignProjectControls,
  runScan,
  getLatestScan
} from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';
import axios from 'axios';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const CONNECTOR_TYPES = [
  { value: 'github', label: 'GitHub', icon: '🐙', description: 'Monitor code repositories and CI/CD events' },
  { value: 's3', label: 'AWS S3', icon: '☁️', description: 'Scan training data and model artifacts' },
  { value: 'openai', label: 'OpenAI', icon: '🤖', description: 'Track API usage and model inference' },
  { value: 'bedrock', label: 'AWS Bedrock', icon: '🏔️', description: 'Monitor AWS Bedrock model usage' },
  { value: 'mlflow', label: 'MLflow', icon: '📊', description: 'Track ML experiments and model lifecycle' },
  { value: 'datadog', label: 'Datadog', icon: '🐶', description: 'Ingest logs and metrics' }
];

const PRESET_OPTIONS = [
  {
    value: 'basic',
    label: 'Basic',
    description: 'Essential controls for getting started (5 controls)',
    badge: 'Recommended for POC'
  },
  {
    value: 'standard',
    label: 'Standard',
    description: 'Comprehensive AI governance coverage (10 controls)',
    badge: 'Most Popular'
  },
  {
    value: 'advanced',
    label: 'Advanced',
    description: 'Full compliance suite with all controls (14 controls)',
    badge: 'Enterprise'
  },
  {
    value: 'soc2',
    label: 'SOC 2 Focus',
    description: 'Controls aligned with SOC 2 requirements',
    badge: 'Compliance'
  }
];

function OnboardingWizard({ visible, onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wizardData, setWizardData] = useState({
    project: null,
    connector: null,
    controls: [],
    preset: 'basic',
    scan: null
  });

  const [form] = Form.useForm();
  const [connectorForm] = Form.useForm();

  // Step 1: Welcome & Project Creation
  const renderWelcomeStep = () => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <RocketOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '24px' }} />
      <Title level={2}>Welcome to TrustGrid!</Title>
      <Paragraph style={{ fontSize: '16px', maxWidth: '600px', margin: '0 auto 32px' }}>
        Let's get you set up in just a few minutes. We'll help you create your first project,
        connect your AI systems, and start monitoring compliance.
      </Paragraph>

      <Card style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
        <Title level={4}>Create Your First Project</Title>
        <Paragraph type="secondary">
          Projects help you organize different AI systems or environments (e.g., Production, Staging, ML Research)
        </Paragraph>

        <Form form={form} layout="vertical" style={{ marginTop: '24px' }}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter a project name' }]}
          >
            <Input placeholder="e.g., Production AI Systems" size="large" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Describe what this project monitors..."
            />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );

  // Step 2: Connector Setup
  const renderConnectorStep = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <ApiOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
        <Title level={3}>Connect Your First Data Source</Title>
        <Paragraph type="secondary">
          Connect to where your AI systems log events. You can add more connectors later.
        </Paragraph>
      </div>

      <Card style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Form form={connectorForm} layout="vertical">
          <Form.Item
            name="connector_type"
            label="Choose a Connector Type"
            rules={[{ required: true, message: 'Please select a connector type' }]}
          >
            <Radio.Group style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {CONNECTOR_TYPES.map(type => (
                  <Radio.Button
                    key={type.value}
                    value={type.value}
                    style={{
                      width: '100%',
                      height: 'auto',
                      padding: '12px 16px',
                      textAlign: 'left',
                      whiteSpace: 'normal'
                    }}
                  >
                    <div>
                      <Text strong>{type.icon} {type.label}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {type.description}
                      </Text>
                    </div>
                  </Radio.Button>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="name"
            label="Connector Name"
            rules={[{ required: true, message: 'Please enter a connector name' }]}
          >
            <Input placeholder="e.g., Production OpenAI API" />
          </Form.Item>

          <Alert
            message="Configuration Details"
            description="After onboarding, you'll configure connection details (API keys, endpoints, etc.) in the connector settings."
            type="info"
            showIcon
          />
        </Form>
      </Card>
    </div>
  );

  // Step 3: Control Selection
  const renderControlsStep = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <SafetyCertificateOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
        <Title level={3}>Choose Your Compliance Controls</Title>
        <Paragraph type="secondary">
          Select a preset that matches your needs. You can customize controls later.
        </Paragraph>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Radio.Group
          value={wizardData.preset}
          onChange={(e) => setWizardData({ ...wizardData, preset: e.target.value })}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {PRESET_OPTIONS.map(preset => (
              <Card
                key={preset.value}
                hoverable
                style={{
                  border: wizardData.preset === preset.value ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
                onClick={() => setWizardData({ ...wizardData, preset: preset.value })}
              >
                <Radio value={preset.value} style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '16px' }}>{preset.label}</Text>
                  {preset.badge && (
                    <Tag color="blue" style={{ marginLeft: '8px' }}>{preset.badge}</Tag>
                  )}
                </Radio>
                <Paragraph type="secondary" style={{ marginLeft: '24px', marginBottom: 0 }}>
                  {preset.description}
                </Paragraph>
              </Card>
            ))}
          </Space>
        </Radio.Group>
      </div>
    </div>
  );

  // Step 4: Configuration
  const renderConfigStep = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <SettingOutlined style={{ fontSize: '48px', color: '#fa8c16', marginBottom: '16px' }} />
        <Title level={3}>Configuration Summary</Title>
        <Paragraph type="secondary">
          Review your setup before running the first scan
        </Paragraph>
      </div>

      <Card style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <Text strong>Project:</Text>
          <div style={{ marginTop: '8px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <Text>{wizardData.project?.name || 'Not created yet'}</Text>
            {wizardData.project?.description && (
              <>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {wizardData.project.description}
                </Text>
              </>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <Text strong>Connector:</Text>
          <div style={{ marginTop: '8px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <Text>{wizardData.connector?.name || 'Not created yet'}</Text>
            {wizardData.connector?.connector_type && (
              <>
                <br />
                <Tag color="blue" style={{ marginTop: '4px' }}>
                  {CONNECTOR_TYPES.find(t => t.value === wizardData.connector.connector_type)?.label}
                </Tag>
              </>
            )}
          </div>
        </div>

        <div>
          <Text strong>Controls Preset:</Text>
          <div style={{ marginTop: '8px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <Text>
              {PRESET_OPTIONS.find(p => p.value === wizardData.preset)?.label}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {PRESET_OPTIONS.find(p => p.value === wizardData.preset)?.description}
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );

  // Step 5: First Scan
  const renderScanStep = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <ScanOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
        <Title level={3}>Running Your First Scan</Title>
        <Paragraph type="secondary">
          We're analyzing your setup and checking compliance controls
        </Paragraph>
      </div>

      <Card style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        {loading ? (
          <div style={{ padding: '40px 0' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: '24px' }}>
              Scanning your project configuration...
            </Paragraph>
          </div>
        ) : wizardData.scan ? (
          <div>
            <CheckCircleOutlined
              style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }}
            />
            <Title level={4}>Scan Complete!</Title>
            <Paragraph>
              Trust Score: <Text strong style={{ fontSize: '24px', color: getTrustScoreColor(wizardData.scan.trust_score) }}>
                {wizardData.scan.trust_score}
              </Text>
            </Paragraph>
          </div>
        ) : (
          <div style={{ padding: '20px 0' }}>
            <Paragraph>Ready to run your first compliance scan</Paragraph>
          </div>
        )}
      </Card>
    </div>
  );

  // Step 6: Review & Complete
  const renderCompleteStep = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <TrophyOutlined style={{ fontSize: '64px', color: '#faad14', marginBottom: '24px' }} />
      <Title level={2}>You're All Set!</Title>
      <Paragraph style={{ fontSize: '16px', maxWidth: '600px', margin: '0 auto 32px' }}>
        Your TrustGrid project is ready. Here's what you can do next:
      </Paragraph>

      <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
        <Card style={{ marginBottom: '16px' }}>
          <Space>
            <ApiOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <div>
              <Text strong>Configure Your Connector</Text>
              <br />
              <Text type="secondary">Add API keys and connection details to start ingesting events</Text>
            </div>
          </Space>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Space>
            <SafetyCertificateOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
            <div>
              <Text strong>Explore Controls</Text>
              <br />
              <Text type="secondary">Review and customize compliance controls for your needs</Text>
            </div>
          </Space>
        </Card>

        <Card style={{ marginBottom: '16px' }}>
          <Space>
            <ScanOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
            <div>
              <Text strong>View Dashboard</Text>
              <br />
              <Text type="secondary">Monitor your trust score and compliance status</Text>
            </div>
          </Space>
        </Card>
      </div>

      {wizardData.scan && (
        <Alert
          message={`Initial Trust Score: ${wizardData.scan.trust_score}`}
          description="This score will improve as you configure connectors and ingest real data."
          type="info"
          showIcon
          style={{ maxWidth: '600px', margin: '32px auto 0' }}
        />
      )}
    </div>
  );

  const getTrustScoreColor = (score) => {
    if (score >= 85) return '#52c41a';
    if (score >= 70) return '#faad14';
    return '#f5222d';
  };

  const handleNext = async () => {
    try {
      setLoading(true);

      if (currentStep === 0) {
        // Step 1: Create project
        const values = await form.validateFields();
        const projectRes = await createProject(values);
        setWizardData({ ...wizardData, project: projectRes.data });

        // Update onboarding state
        await axios.post('/api/trustgrid/v1/onboarding/step/1/complete', {
          project_id: projectRes.data.id
        });

        showSuccess('Project created successfully');
        setCurrentStep(1);

      } else if (currentStep === 1) {
        // Step 2: Create connector
        const values = await connectorForm.validateFields();
        const connectorRes = await createConnector(wizardData.project.id, {
          ...values,
          config: JSON.stringify({ placeholder: 'Configure later' })
        });
        setWizardData({ ...wizardData, connector: connectorRes.data });

        await axios.post('/api/trustgrid/v1/onboarding/step/2/complete');
        showSuccess('Connector created successfully');
        setCurrentStep(2);

      } else if (currentStep === 2) {
        // Step 3: Assign controls (using preset)
        const presetRes = await axios.get(`/api/trustgrid/v1/presets/${wizardData.preset}`);
        const controlCodes = presetRes.data.controls.map(c => c.code);

        await assignProjectControls(wizardData.project.id, controlCodes);
        setWizardData({ ...wizardData, controls: controlCodes });

        await axios.post('/api/trustgrid/v1/onboarding/step/3/complete');
        showSuccess('Controls configured successfully');
        setCurrentStep(3);

      } else if (currentStep === 3) {
        // Step 4: Review (just move to next)
        await axios.post('/api/trustgrid/v1/onboarding/step/4/complete');
        setCurrentStep(4);

      } else if (currentStep === 4) {
        // Step 5: Run first scan
        const scanRes = await runScan(wizardData.project.id);

        // Poll for scan completion
        let scanComplete = false;
        let attempts = 0;
        while (!scanComplete && attempts < 10) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const latestScan = await getLatestScan(wizardData.project.id);
          if (latestScan.data.status === 'completed') {
            setWizardData({ ...wizardData, scan: latestScan.data });
            scanComplete = true;
          }
          attempts++;
        }

        await axios.post('/api/trustgrid/v1/onboarding/step/5/complete');
        showSuccess('First scan completed');
        setCurrentStep(5);

      } else if (currentStep === 5) {
        // Step 6: Complete onboarding
        await axios.post('/api/trustgrid/v1/onboarding/step/6/complete');
        showSuccess('Onboarding completed!');
        onComplete(wizardData);
      }

    } catch (error) {
      console.error('Onboarding step failed:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  const handleSkip = async () => {
    try {
      await axios.post('/api/trustgrid/v1/onboarding/skip', {
        reason: 'User skipped onboarding wizard'
      });
      onSkip();
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      showError(error);
    }
  };

  const steps = [
    { title: 'Welcome', icon: <RocketOutlined /> },
    { title: 'Connector', icon: <ApiOutlined /> },
    { title: 'Controls', icon: <SafetyCertificateOutlined /> },
    { title: 'Review', icon: <SettingOutlined /> },
    { title: 'Scan', icon: <ScanOutlined /> },
    { title: 'Complete', icon: <TrophyOutlined /> }
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderWelcomeStep();
      case 1: return renderConnectorStep();
      case 2: return renderControlsStep();
      case 3: return renderConfigStep();
      case 4: return renderScanStep();
      case 5: return renderCompleteStep();
      default: return null;
    }
  };

  return (
    <Modal
      open={visible}
      title="TrustGrid Setup Wizard"
      width={900}
      footer={null}
      closable={false}
      maskClosable={false}
    >
      <Steps current={currentStep} style={{ marginBottom: '32px' }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      <div style={{ minHeight: '400px' }}>
        {renderStepContent()}
      </div>

      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {currentStep === 0 && (
            <Button onClick={handleSkip}>
              Skip Setup
            </Button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {currentStep > 0 && currentStep < 5 && (
            <Button onClick={handlePrevious} disabled={loading}>
              Previous
            </Button>
          )}

          {currentStep < 5 ? (
            <Button
              type="primary"
              onClick={handleNext}
              loading={loading}
              className="custom-button"
            >
              {currentStep === 0 ? 'Create Project' :
               currentStep === 1 ? 'Add Connector' :
               currentStep === 2 ? 'Configure Controls' :
               currentStep === 3 ? 'Start Scan' :
               'Continue'}
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleNext}
              className="custom-button"
            >
              Go to Dashboard
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default OnboardingWizard;
